import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { TableSortLabel, Typography } from "@mui/material";
import React, { useState } from "react";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import { Stack } from "@mui/system";

interface Row {
  Metric: any;
  Predicted: number;
  Actual: number;
  Deviation: number;
}

interface Props {
  tableHeadings: { [key: string]: string };
  rows: Row[];
  selectedFilter: string;
}

const MonthlySummaryTable = ({ rows, selectedFilter }: Props) => {
  const customOrder = [
    "growth",
    "active_users",
    "old_active_users",
    "acquired_active_users",
    "churn_rate",
    "ftd",
    "Ratio_between_acquired_to_ftd",
    "avg_net_rev_per_active_user",
    "net_rev",
    "avg_rs_p",
    "rs_br_ng",
    "to_rs_active_ratio",
    "rs_income_dollar",
    "rs_pv",
    "cpa_pv",
    "cpa_income_dollar",
    "total_income",
    "CVR",
    "clicks",
    "EPC",
    "EPC_CPA",
    "EPC_RS",
    "CPC",
    "CAC",
    "spend",
    "profit"
  ];

  const metricSymbols: Record<string, string> = {
    growth: '%',
    churn_rate: '%',
    Ratio_between_acquired_to_ftd: '%',
    avg_rs_p: '%',
    to_rs_active_ratio: '%',
    rs_br_ng: '%',
    CVR: '%',
    avg_net_rev_per_active_user: '$',
    net_rev: '$',
    rs_income_dollar: '$',
    rs_pv: '$',
    cpa_pv: '$',
    cpa_income_dollar: '$',
    total_income: '$',
    EPC_CPA: '$',
    EPC_RS: '$',
    EPC: '$',
    CPC: '$',
    CAC: '$',
    spend: '$',
    profit: '$'
  };

  const filteredRows = rows?.filter(row => {
    switch (selectedFilter) {
      case "Key Metrics":
        return ["spend", "clicks", "ftd", "CVR", "CAC", "profit"].includes(row.Metric);
      case "Income":
        return ["cpa_income_dollar", "rs_income_dollar", "total_income", "cpa_pv", "rs_pv", "net_rev", "avg_rs_p", "EPC", "EPC_CPA", "EPC_RS"].includes(row.Metric);
      case "Performance":
        return ["growth", "active_users", "old_active_users", "acquired_active_users", "churn_rate", "CPC", "Ratio_between_acquired_to_ftd", "avg_net_rev_per_active_user", "rs_br_ng"].includes(row.Metric);
      case "All":
      default:
        return true;
    }
  });

  const [sort, setSort] = useState<{ keytoSort: string | undefined, direction: 'asc' | 'desc' | undefined }>({ keytoSort: 'Metric', direction: 'desc' });

  const tableheaders = [
    { id: 0, KEY: "Metric", label: "Metric", sortable: false },
    { id: 1, KEY: "Predicted", label: "Predicted", sortable: true },
    { id: 2, KEY: "Actual", label: "Actual", sortable: true },
    { id: 3, KEY: "Deviation", label: "Deviation", sortable: true }
  ];

  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable
    setSort({
      keytoSort: header.KEY,
      direction: header.KEY === sort.keytoSort && sort.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  const getSortedData = React.useCallback((arrayToSort: any[]) => {
    if (!sort.keytoSort || !sort.direction) {
      return arrayToSort;
    }

    return arrayToSort.slice().sort((a, b) => {
      const keytoSort = sort.keytoSort;
      if (keytoSort === undefined) {
        return 0;
      }
      let aValue, bValue;

      if (keytoSort === 'month') {
        const [monthA, yearA] = a[keytoSort].split('/').map(Number);
        const [monthB, yearB] = b[keytoSort].split('/').map(Number);
        aValue = yearA * 100 + monthA;
        bValue = yearB * 100 + monthB;
      } else {
        aValue = a[keytoSort];
        bValue = b[keytoSort];
      }

      if (aValue === undefined) return 1;
      if (bValue === undefined) return -1;

      if (aValue === "") return 1;
      if (bValue === "") return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return sort.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      } else {
        return sort.direction === "asc"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }
    });
  }, [sort]);

  const sortedRows = selectedFilter === "All" ? customOrder.map((key) => filteredRows.find((row) => row.Metric === key)).filter(Boolean) : getSortedData(filteredRows || []);

  const allValuesNull = sortedRows?.every(row =>
    (row.Actual === null || row.Actual === "") &&
    (row.Predicted === null || row.Predicted === "") &&
    (row.Deviation === null || row.Deviation === "")
  );

  const formatMetricName = (metric: string) => {
    return metric?.split('_')?.join(' ')?.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const getTrendIcon = (_metric: string, _predicted: number, _actual: number, deviation: number) => {
    if (deviation > 0) {
      return <TrendingUpIcon sx={{ marginRight: "8px", color: "#2E7D32" }} />; // Green up arrow for positive
    } else {
      return <TrendingDownIcon sx={{ marginRight: "8px", color: "#D32F2F" }} />; // Red down arrow for negative
    }
  };

  const formatNumber = (value: any): string => {
    if (value === null || value === undefined || value === '') return '0';
    const num = Number(Math.round(value));
    if (isNaN(num)) return '0';
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  return (
    <>
      <TableContainer sx={{ scrollbarWidth: "thin", marginTop: "16px" }}>
        <Table aria-label="collapsible table" size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {tableheaders.map((header, index) => (
                <TableCell
                  key={index}
                  align="left"
                >
                  {selectedFilter === "All" ? (
                    header.label
                  ) : (
                    <TableSortLabel
                      active={sort.keytoSort === header.KEY}
                      direction={sort.keytoSort === header.KEY ? sort.direction : 'asc'}
                      onClick={() => handleHeaderClick(header)}
                      disabled={!header.sortable}
                    >
                      {header.label}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {allValuesNull ? (
              <TableRow>
                <TableCell colSpan={tableheaders.length} align="center">
                  <Typography variant="h6">No Data Available</Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row: Row, rowIndex: any) => (
                <React.Fragment key={rowIndex}>
                  <TableRow
                    sx={{ "& > *": { borderBottom: "1px solid rgba(224, 224, 224, 1) !important", }, }}>
                    <TableCell align="left">
                      {formatMetricName(row?.Metric)}
                    </TableCell>
                    <TableCell>
                      {metricSymbols[row.Metric] === '%' 
                        ? `${Math.round(Number(row?.Predicted * 100))}${metricSymbols[row.Metric]}`
                        : `${formatNumber(row.Predicted)}${metricSymbols[row.Metric] || ''}`
                      }
                    </TableCell>
                    <TableCell>
                      {metricSymbols[row.Metric] === '%' 
                        ? `${Math.round(Number(row?.Actual * 100))}${metricSymbols[row.Metric]}`
                        : `${formatNumber(row.Actual)}${metricSymbols[row.Metric] || ''}`
                      }
                    </TableCell>
                    <TableCell align="left">
                      <Stack direction="row" alignItems={"center"}>
                        {getTrendIcon(row?.Metric, row?.Predicted, row?.Actual, row?.Deviation)}
                        {`${Math.round(Number(row?.Deviation * 100))}%`}
                      </Stack>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default MonthlySummaryTable;
